import { DeliveryStopPod } from "api/response";
import { formatDate } from "helpers/dateTimeHelper";

const SIMPLIFIED_DATE_FORMAT = "D MMM";
const DATE_ONLY_PATTERN = /^(\d{4}-\d{2}-\d{2})/;

export type AssociatedStopScheduleWindow = {
  id?: number;
  stop_id?: number;
  stop_time_type?: string | null;
  time_type?: string | null;
  stop_after_datetime?: string | null;
  stop_before_datetime?: string | null;
  stop_after_date?: string | null;
  stop_before_date?: string | null;
};

function getAssociatedStopCity(locationTitle?: string | null): string | undefined {
  const city = locationTitle?.split(",")[0]?.trim();
  return city || undefined;
}

function toDateOnly(value?: string | null): string | undefined {
  if (!value) return undefined;
  return DATE_ONLY_PATTERN.exec(value)?.[1];
}

function formatSimplifiedStopDate(date?: string | null): string | undefined {
  const dateOnly = toDateOnly(date);
  if (!dateOnly) return undefined;
  const formatted = formatDate(dateOnly, SIMPLIFIED_DATE_FORMAT);
  return formatted === "Invalid date" ? undefined : formatted;
}

function isBetweenWindow(scheduleStop?: AssociatedStopScheduleWindow | null): boolean {
  const type = String(scheduleStop?.stop_time_type ?? scheduleStop?.time_type ?? "").toLowerCase();
  return type === "between";
}

function getScheduleWindowDates(
  scheduleStop?: AssociatedStopScheduleWindow | null,
): { start?: string; end?: string } {
  return {
    start: scheduleStop?.stop_after_date || scheduleStop?.stop_after_datetime || undefined,
    end: scheduleStop?.stop_before_date || scheduleStop?.stop_before_datetime || undefined,
  };
}

function formatAssociatedStopDateRange(
  deliveryStop: Pick<DeliveryStopPod, "provider_date">,
  scheduleStop?: AssociatedStopScheduleWindow | null,
): string | undefined {
  if (isBetweenWindow(scheduleStop)) {
    const { start, end } = getScheduleWindowDates(scheduleStop);
    const startLabel = formatSimplifiedStopDate(start);
    const endLabel = formatSimplifiedStopDate(end);

    if (startLabel && endLabel) {
      return startLabel === endLabel ? startLabel : `${startLabel} - ${endLabel}`;
    }
  }

  return formatSimplifiedStopDate(deliveryStop.provider_date);
}

export function formatAssociatedStopLabel(
  deliveryStop?: Pick<DeliveryStopPod, "location_title" | "provider_date"> | null,
  scheduleStop?: AssociatedStopScheduleWindow | null,
): string | undefined {
  if (!deliveryStop) return undefined;

  const city = getAssociatedStopCity(deliveryStop.location_title);
  const dateLabel = formatAssociatedStopDateRange(deliveryStop, scheduleStop);

  if (city && dateLabel) return `${city}, ${dateLabel}`;
  return city || dateLabel;
}

export function resolveAssociatedStopLabel(
  stop: { id?: number } | null | undefined,
  deliveryStops?: DeliveryStopPod[] | null,
  scheduleStops?: AssociatedStopScheduleWindow[] | null,
): string | undefined {
  if (!stop?.id) return undefined;

  const deliveryStop = deliveryStops?.find((candidate) => candidate.stop_id === stop.id);
  if (!deliveryStop) return undefined;

  const scheduleStop = scheduleStops?.find(
    (candidate) => candidate.stop_id === stop.id || candidate.id === stop.id,
  );

  return formatAssociatedStopLabel(deliveryStop, scheduleStop);
}
